import { useRef, useState } from "react";
import { BrowserOption, clearDependentFields, getNextKey, HierarcyKey, isHierarchyKey, ModelBrowserOptions, transformForAutoComplete } from "./util";
import { browseMotorAtlasOptions } from "../../../services/motorAtlasService";
import { renderErrorMessage } from "../../../components/messages/errorMessage";

type Mode = "select" | "autocomplete"

export default function useMotorAtlasOptions(mode: Mode = "select", extendedLookup: boolean = false) {
  const [loadingOptionsFields, setLoadingOptionsFields] = useState<Set<keyof ModelBrowserOptions>>(new Set())
  const reqIdRef = useRef(0);
  const rawOptions = useRef<ModelBrowserOptions>({
    type: [
      { value: "01", label: "Personbil" },
      { value: "02", label: "Varebil" },
      { value: "03", label: "Motorcykel" },
    ],
    make: [],
    model: [],
    subModel: [],
    variant: [],
  })
  const [options, setOptions] = useState<ModelBrowserOptions>({
    type: [
      { value: "01", label: "Personbil" },
      { value: "02", label: "Varebil" },
      { value: "03", label: "Motorcykel" },
    ],
    make: [],
    model: [],
    subModel: [],
    variant: [],
  })
  // label → code (for autocomplete) or passthrough (for select)
  const labelToCode = (key: HierarcyKey, input?: string): string | undefined => {
    if (!input) return undefined;
    if (mode === "select") return input; // value IS the code
    const found = rawOptions.current[key]?.find(
      o => o.label.toLowerCase().trim() === input.toLowerCase().trim()
    );
    return found?.value;
  };


  const stopLoadingKey = (nextKey: HierarcyKey) => {
    setLoadingOptionsFields(prev => {
      const s = new Set(prev)
      s.delete(nextKey)
      return s
    })
  }


  async function handleDrillDown<T extends Partial<Record<HierarcyKey, string | undefined>>>(values: T, changedKey?: keyof T) {
    if (!changedKey || !isHierarchyKey(changedKey)) return;
    const nextKey = getNextKey(changedKey)
    if (nextKey) setLoadingOptionsFields(prev => new Set(prev).add(nextKey));

    const params: Parameters<typeof browseMotorAtlasOptions>[0] = {
      type: values.type,
      make: labelToCode("make", values.make),
      model: labelToCode("model", values.model),
      subModel: labelToCode("subModel", values.subModel),
    } as const;


    // If the changed key has a non-empty value that does NOT resolve to a code,
    // clear downstream and bail (free text / unknown value).
    const changedResolved = isHierarchyKey(changedKey) ? params[changedKey] : undefined;
    const changedProvided = (values[changedKey as keyof T] ?? "") !== "";
    if (changedProvided && !changedResolved) {
      rawOptions.current = clearDependentFields(rawOptions.current, changedKey, []);
      setOptions(prev => clearDependentFields(prev, changedKey, []));
      if (nextKey) stopLoadingKey(nextKey);
      return; // don’t call the API with unknown/free-text
    }

    if (!params.type) {
      if (nextKey) stopLoadingKey(nextKey)
      return;
    }

    const reqId = ++reqIdRef.current;
    try {
      const cleanedParams = clearDependentFields(params, changedKey, undefined)

      if (reqId !== reqIdRef.current) return;
      const { data: { data } } = await browseMotorAtlasOptions(cleanedParams, extendedLookup)
      if (nextKey) {
        rawOptions.current = {
          ...clearDependentFields(rawOptions.current, changedKey, []),
          [nextKey]: data,
        };

        // update UI options
        setOptions(prev => {
          const cleaned = clearDependentFields(prev, changedKey, []);
          return {
            ...cleaned,
            [nextKey]: mode === "autocomplete" ? transformForAutoComplete(data) : data,
          };
        });
      }
    } catch (error) {
      console.error(error)
      renderErrorMessage(error)
    } finally {
      if (nextKey) stopLoadingKey(nextKey)
    }
  }


  return {
    options,
    rawOptions,
    loadingOptionsFields,
    handleDrillDown
  }
}
